import { useEffect, useRef, RefObject } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export interface ScrollRevealOptions {
  /** Distance the element travels in from below, in px. */
  fromY?: number;
  /** Optional initial scale (e.g. 0.97 for cards). */
  fromScale?: number;
  /** Animation duration, seconds. */
  duration?: number;
  /** Stagger when reveal targets multiple matched elements via `selector`. */
  stagger?: number;
  /** ScrollTrigger start position (e.g. "top 80%"). */
  start?: string;
  /** When set, reveal all descendants matching the selector with stagger. */
  selector?: string;
  /** When set, use this as the trigger element instead of the ref itself. */
  triggerSelf?: boolean;
}

// Reusable GSAP fade-in-up scroll reveal. Replaces the boilerplate gsap.context
// + fromTo + scrollTrigger pattern repeated across nearly every section.
//
// Usage A (single element):
//   const ref = useScrollReveal<HTMLDivElement>();
//   <div ref={ref} style={{ opacity: 0 }}>...</div>
//
// Usage B (children with selector + stagger):
//   const ref = useScrollReveal<HTMLDivElement>({ selector: '.card', stagger: 0.1 });
//   <div ref={ref}>{cards.map(c => <div className="card" style={{ opacity: 0 }}>...</div>)}</div>
export default function useScrollReveal<T extends HTMLElement = HTMLElement>(
  opts: ScrollRevealOptions = {}
): RefObject<T | null> {
  const {
    fromY = 26,
    fromScale,
    duration = 0.7,
    stagger = 0.1,
    start = 'top 82%',
    selector,
  } = opts;

  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    const ctx = gsap.context(() => {
      const targets = selector
        ? ref.current!.querySelectorAll(selector)
        : ref.current!;

      const fromVars: gsap.TweenVars = { opacity: 0, y: fromY };
      const toVars: gsap.TweenVars = {
        opacity: 1, y: 0, duration, ease: 'power2.out',
        scrollTrigger: { trigger: ref.current!, start },
      };
      if (fromScale !== undefined) {
        fromVars.scale = fromScale;
        toVars.scale = 1;
      }
      if (selector) {
        toVars.stagger = stagger;
      }

      gsap.fromTo(targets, fromVars, toVars);
    });
    return () => ctx.revert();
  }, [fromY, fromScale, duration, stagger, start, selector]);

  return ref;
}
